import { type DateTimeContextProps } from '@rtkwlf/fenrir-react';
import type {
  AgentScanType,
  CronSchedule,
  CronWindow,
  ScheduleRow,
} from '@rtkwlf/rendall';
import { Frequency, ScheduleStatus, SourceType } from '@rtkwlf/rendall';
import dayjs from 'dayjs';
import type { TFunction } from 'i18next';
import isEmpty from 'lodash/isEmpty';
import { stringify } from 'query-string';
import {
  generatePath,
  type Location,
  type NavigateFunction,
} from 'react-router-dom';
import type { QueryParamConfigMap } from 'use-query-params';
import { encodeQueryParams, StringParam } from 'use-query-params';

import type { FilterObjType, FilterType } from 'ae-components/filter/Filter';
import {
  TELEMETRY_EDIT_SCAN_SCHEDULE_ROUTE,
  TELEMETRY_SCAN_SCHEDULES_ROUTE,
} from 'ae-constants/routes.constants';

import type {
  FormSubmissionData,
  PriorityOptions,
} from './components/new-scan-schedules/form-types';

export const TIME_VALUE_KEYS = [
  'scanMonthlyOnDay',
  'scanDate',
  'scanTime',
  'frequency',
  'scanTimeHourAndMinutes',
];

enum SearchFieldsType {
  Source = 'source',
  Name = 'name',
  Description = 'description',
  ScanningEnabled = 'scanningEnabled',
  Status = 'status',
  FrequencyField = 'frequency',
  LastScan = 'lastScan',
  NextScan = 'nextScan',
  ScannerName = 'scannerName',
  Targets = 'targets',
  ScannerId = 'scannerId',
}

const searchFields = Object.values(SearchFieldsType);

export enum QueuedScheduleStatus {
  Queued = 'queued',
}
export type ScanScheduleStatus = QueuedScheduleStatus | ScheduleStatus;

export type ScanSchedulesFiltersObject = {
  search?: string;
  source?: SourceType[];
  status?: ScanScheduleStatus[];
  frequency?: Frequency[];
};

export const scanDateFormat = 'YYYY-MM-DD';
export const scanTimeFormat = 'hh:mm A';
export const dateTimeFormat = `${scanDateFormat} ${scanTimeFormat}`;

export const filterList = ['search', 'source', 'status', 'frequency'];

export const handleEditSchedule = (
  id: string,
  navigate: NavigateFunction,
  source: SourceType,
  scannerId: string,
  searchParams?: string,
) => {
  switch (source) {
    case SourceType.Iva: {
      const configMap = {
        scannerId: StringParam,
        source: StringParam,
      };
      const apiParams = encodeQueryParams(configMap, {
        scannerId,
        source,
      });
      const route = generatePath(
        `${TELEMETRY_EDIT_SCAN_SCHEDULE_ROUTE.replace(':scheduleId', id)}`,
      );
      navigate(`${route}?${stringify(apiParams)}`, {
        state: { searchParams: searchParams || '' },
      });

      break;
    }
    case SourceType.Agent: {
      const configMap = { source: StringParam };
      const apiParams = encodeQueryParams(configMap, {
        source,
      });
      const route = generatePath(
        `${TELEMETRY_EDIT_SCAN_SCHEDULE_ROUTE.replace(':scheduleId', id)}`,
      );
      navigate(`${route}?${stringify(apiParams)}`, {
        state: { searchParams },
      });

      break;
    }
    default: {
      break;
    }
  }
};

export const getScanSchedulesFilters = (t: TFunction): FilterType[] => [
  {
    field: SearchFieldsType.Source,
    isSingleSelect: false,
    placeholderKey: t('ui:dropDown.all'),
    titleKey: t('telemetryScanSchedules:createNewScanForm.source'),
    values: Object.values(SourceType).map((s) =>
      t(`telemetryScanSchedules:sourceTypes.${s}`),
    ),
  },
  {
    field: SearchFieldsType.Status,
    isSingleSelect: true,
    placeholderKey: t('ui:dropDown.all'),
    titleKey: t('telemetryScanSchedules:status'),
    values: Object.values(ScheduleStatus).map((s) =>
      t(`telemetryScanSchedules:statusTypes.${s}`),
    ),
  },
  {
    field: SearchFieldsType.FrequencyField,
    isSingleSelect: true,
    placeholderKey: t('ui:dropDown.all'),
    titleKey: t('telemetryScanSchedules:frequency'),
    values: Object.values(Frequency).map((s) =>
      t(`telemetryScanSchedules:frequencyTypes.${s}`),
    ),
  },
];

export interface ScheduleRowSchema extends ScheduleRow {
  searchField?: string;
}

/**
 * Generate scheduleRowSearchField for search filter
 */
type TimeZone = DateTimeContextProps['timezone'];
export const scheduleRowSearchField = (
  row: ScheduleRow,
  t: TFunction,
  timezone: TimeZone,
  getDateTimeByTz: (dateTimeLog: string, timezone: TimeZone) => string,
) => {
  const searchableText: string[] = [];
  searchFields.forEach((fieldKey) => {
    switch (fieldKey) {
      case SearchFieldsType.ScanningEnabled:
        searchableText.push(
          (row[SearchFieldsType.ScanningEnabled]
            ? t('telemetryScanSchedules:enabled')
            : t('telemetryScanSchedules:disabled')
          ).toLocaleLowerCase(),
        );
        break;
      case SearchFieldsType.NextScan: {
        const tzNextScan = row.nextScan
          ? getDateTimeByTz(row.nextScan, timezone)
          : '';
        searchableText.push(tzNextScan.toLocaleLowerCase());
        break;
      }
      case SearchFieldsType.LastScan: {
        const tzLastScan = row.lastScan
          ? getDateTimeByTz(row.lastScan, timezone)
          : '';
        searchableText.push(tzLastScan.toLocaleLowerCase());
        break;
      }
      case SearchFieldsType.Status:
        searchableText.push(row.status.toLocaleLowerCase());
        break;
      case SearchFieldsType.ScannerName: {
        const scannerName = row.scannerName
          ? row.scannerName.toLocaleLowerCase()
          : '';
        searchableText.push(scannerName);
        break;
      }
      case SearchFieldsType.Targets: {
        const targetsValues = row[fieldKey].map(({ ipAddress, name }) => {
          if (row.source === SourceType.Iva) {
            return isEmpty(ipAddress) ? '' : ipAddress;
          }
          return isEmpty(name) ? '' : name;
        });
        if (targetsValues.length)
          searchableText.push(
            JSON.stringify(targetsValues).toLocaleLowerCase(),
          );
        break;
      }
      case SearchFieldsType.Source:
      case SearchFieldsType.Name:
      case SearchFieldsType.Description:
      case SearchFieldsType.FrequencyField:
      case SearchFieldsType.ScannerId:
        searchableText.push(row[fieldKey].toString().toLocaleLowerCase());
        break;
    }
  });

  return searchableText.join('');
};

const getLocaleKey = (input: string): string => {
  if (input === SearchFieldsType.FrequencyField) return 'frequencyTypes';
  if (input === SearchFieldsType.Status) return 'statusTypes';
  if (input === SearchFieldsType.Source) return 'sourceTypes';

  return input;
};

export const getFilters = (
  input: QueryParamConfigMap,
  t: TFunction,
): FilterObjType => {
  const output: FilterObjType = {};

  Object.entries(input).forEach(([key, values]) => {
    if (Array.isArray(values) && values.length > 0) {
      if (key === 'search') {
        output[key] = values.toString();
      } else {
        output[key] = values.map((v) =>
          t(
            `telemetryScanSchedules:${getLocaleKey(
              key,
            )}.${v.toLocaleLowerCase()}`,
          ),
        );
      }
    }
  });
  return output;
};

export const weeklyDaysCronToBoolean = (
  cronArray: CronWindow[] | undefined,
): Array<boolean> => {
  /**
   * Convert schedule cron objects returned from the api
   * into the array of boolean format needed by
   * the Schedule Forms.
   */

  // create baseline array with all 7 days disabled
  const daysArray = Array(7).fill(false);

  // return the baseline if there is no cron array or the array is empty
  // special consideration for BaconSecurity, won't happen for normal usage
  if (!cronArray || !cronArray.length) return daysArray;

  // case for weekly with single day or newer cron format
  // dayOfWeek: [5]  or  dayOfWeek: [5, 6]
  if (cronArray.length === 1)
    return [...Array(7).keys()].map(
      (i) => cronArray[0].cron?.dayOfWeek?.includes(i) || false,
    );

  // case for older legacy format with multiple days coming from MRD
  // [{...dayOfWeek: [5], ...}, {...dayOfWeek: [6]},...]
  cronArray.forEach(({ cron }: CronWindow) => {
    if (cron?.dayOfWeek) daysArray[cron.dayOfWeek[0]] = true;
  });

  return daysArray;
};

export const getScanWindow = (
  scanWindowInMinutes: number | undefined,
): string => {
  if (!scanWindowInMinutes) return '';
  return Math.ceil(scanWindowInMinutes / 60).toString();
};

export const getTimeHourAndMinutesValues = (
  hour: number[] | undefined = [0],
  minutes: number[] | undefined = [0],
): string => {
  return dayjs().hour(hour[0]).minute(minutes[0]).format('HH:mm');
};

export const getFormState = (state: ScheduleRow): FormSubmissionData => {
  let scanTypes: AgentScanType[] = [];

  scanTypes = state.scanTypes.map(
    (scanTypeObj) => scanTypeObj.type,
  ) as AgentScanType[];

  const formState: FormSubmissionData = {
    description: state.description,
    enabled: state.scanningEnabled,
    frequency: state.frequency,
    name: state.name,
    priority: `${state.priority}` as unknown as PriorityOptions,
    scanMonth:
      state.source === SourceType.Agent
        ? state.windows[0].cron?.month?.toString()
        : undefined,
    scanTypes,
    scanWeeklyOnDayAgent:
      state.source === SourceType.Agent
        ? state.windows[0].cron?.dayOfWeek?.toString()
        : undefined,
    scannerId: state.scannerId,
    timezone: state.timezone,
  };

  if (state.frequency !== Frequency.Continuous && state.windows.length) {
    const utcCronArray: CronWindow[] = state.windows;
    const { hour, minute, dayOfMonth, year } = utcCronArray[0]
      .cron as CronSchedule;
    const scanWindow = utcCronArray[0].lengthMinute;
    const convertedWeeklyDays = weeklyDaysCronToBoolean(state.windows);
    formState.scanTimeHourAndMinutes = getTimeHourAndMinutesValues(
      hour,
      minute,
    );
    formState.scanYear = year ? year[0].toString() : '';
    formState.scanWindow = getScanWindow(scanWindow);
    formState.scanMonthlyOnDay = `${dayOfMonth || ''}`;
    formState.scanWeeklyOnDay = convertedWeeklyDays;
  }

  if (state.source === SourceType.Iva) {
    formState.targets = state.targets.map((t) => t.ipAddress) as string[];
    formState.scanningOutsideWindowDisabled =
      state.scanningOutsideWindowDisabled;
  } else if (state.source === SourceType.Agent) {
    formState.targets = [...state.targets];
    formState.autoEnroll = !!state.autoEnrollNewClients;
  }

  return formState;
};

export const goBackToTable = (
  navigate: NavigateFunction,
  location: Location,
) => {
  if (location.state) {
    navigate(-1);
  } else {
    navigate(generatePath(TELEMETRY_SCAN_SCHEDULES_ROUTE));
  }
};
